import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Type } from "@themes/pollinator/ui/Type";
import { DataTable } from "@themes/pollinator/ui/DataTable";

export type TeamMember = {
  id: number;
  name: string;
  email: string;
  status: "active" | "pending";
  /** True when the invitation behind a pending row can no longer be claimed. */
  isExpired?: boolean;
};

export type Props = {
  teamMembers: TeamMember[];
};

export function CoworkersTable({ teamMembers }: Props) {
  const columns = useMemo<ColumnDef<TeamMember>[]>(
    () => [
      {
        accessorKey: "name",
        header: () => "Name",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col">
              {row.original.name && (
                <Type variant="h6" className="truncate max-w-xs">
                  {row.original.name}
                </Type>
              )}
              <Type variant="caption">{row.original.email}</Type>
            </div>
          );
        },
        filterFn: "fuzzy",
        enableGlobalFilter: true,
      },
      {
        accessorKey: "status",
        header: () => "Status",
        cell: ({ row }) => {
          const isPending = row.original.status === "pending";
          // A pending row for a dead invitation reads as "still out there
          // working", which sends admins chasing email deliverability instead
          // of reissuing the invitation.
          const isExpired = isPending && row.original.isExpired === true;

          if (isExpired) {
            return (
              <Type variant="h6" className="text-error">
                Expired
              </Type>
            );
          }

          return (
            <Type variant="h6">{isPending ? "Pending" : "Active"}</Type>
          );
        },
      },
    ],
    []
  );

  return (
    <DataTable
      data={teamMembers}
      columns={columns}
      isWithSearch={false}
      initialPageSize={25}
    />
  );
}

export default CoworkersTable;

